import {
  RiArrowRightSLine,
  RiCheckLine,
  RiEyeOffLine,
  RiResetLeftLine,
  RiStarLine,
} from "@remixicon/react";
import { Button } from "@screener/uikit/components/button";
import { CheckboxIndicator } from "@screener/uikit/components/checkbox";
import { Field, FieldError } from "@screener/uikit/components/field";
import { Input } from "@screener/uikit/components/input";
import { Label } from "@screener/uikit/components/label";
import { LockedInline } from "@screener/uikit/components/locked-inline";
import { SectionLabel } from "@screener/uikit/components/section-label";
import { Switch } from "@screener/uikit/components/switch";
import { ToggleTile } from "@screener/uikit/components/toggle-tile";
import { Tooltip, TooltipContent, TooltipTrigger } from "@screener/uikit/components/tooltip";
import { cn } from "@screener/uikit/lib/utils";
import { type ReactNode, type RefObject, useId, useRef, useState } from "react";
import {
  AMOUNT_HINT,
  type ExchangeToggleState,
  exchangeLabel,
  exchangeSummary,
  exchangeToggleLabel,
  FILTER_TEXT,
  formatChipUsd,
  MIN_USD_QUICK_VALUES,
  marketLetter,
  marketToggleLabel,
  selectedQuickMinUsd,
  stateLabel,
  toggleQuickMinUsd,
} from "@/lib/labels";
import { usePlanGate } from "@/lib/plan-gate-context";
import {
  activeFilterCount,
  clearFilter,
  defaultFilters,
  exchangeFilterActive,
  exchangesOf,
  MARKET_ORDER,
  marketsOf,
  toggleExchange,
  toggleMarket,
} from "./derive";
import { ExchangeLogo } from "./exchange-logo";
import type { FilterPanelProps } from "./filter-bar";
import {
  editMinUsdDraft,
  type MinUsdDraft,
  minUsdText,
  reconcileMinUsdDraft,
} from "./min-usd-draft";
import { StageCount, VenueCount } from "./screen-count-leaves";
import { FILTERABLE_STATES } from "./stream-state";

type SectionsProps = Pick<FilterPanelProps, "filters" | "onChange" | "venues" | "counts">;

function toggle<T>(values: T[], value: T): T[] {
  return values.includes(value) ? values.filter((item) => item !== value) : [...values, value];
}

type SectionName = "size" | "stage" | "exchange" | "marks";

/** Раздел панели: колонка с рамкой слева (кроме первой) и отступами макета. */
function FilterSection({
  name,
  children,
  className,
}: {
  name: SectionName;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      data-filter-section={name}
      className={cn(
        "flex min-w-0 flex-col gap-2.5 px-4 pt-3.5 pb-4",
        name !== "size" && "border-t border-border lg:border-t-0 lg:border-l",
        className,
      )}
    >
      {children}
    </div>
  );
}

/**
 * Шапка раздела: заголовок слева, справа ссылка сброса. Ссылка есть, только
 * пока раздел отличается от умолчания (`onReset` не передан — её нет).
 */
function FilterSectionHeader({
  id,
  title,
  titleRender,
  summary,
  resetLabel,
  onReset,
}: {
  /** Идентификатор заголовка: на него ссылается `aria-labelledby` ряда управляющих элементов. */
  id?: string;
  title: string;
  titleRender?: React.ReactElement<Record<string, unknown>>;
  summary?: string;
  resetLabel?: string;
  onReset?: () => void;
}) {
  return (
    <div className="flex h-[18px] items-center justify-between gap-2">
      <div className="flex min-w-0 items-baseline gap-2">
        <SectionLabel id={id} render={titleRender} className="font-medium">
          {title}
        </SectionLabel>
        {summary !== undefined ? (
          <span className="text-[11px] font-medium text-foreground">{summary}</span>
        ) : null}
      </div>
      {onReset !== undefined ? (
        <Button
          variant="link"
          size="xs"
          onClick={onReset}
          className="h-[18px] px-0 text-xs font-normal text-muted-foreground hover:text-foreground hover:no-underline"
        >
          {resetLabel}
        </Button>
      ) : null}
    </div>
  );
}

const HINT = "text-[11px] leading-[1.4] text-muted-foreground";

function SizeSection({
  filters,
  onChange,
  draft,
  setDraft,
  inputRef,
}: Pick<SectionsProps, "filters" | "onChange"> & {
  draft: MinUsdDraft;
  setDraft: (draft: MinUsdDraft) => void;
  inputRef: RefObject<HTMLInputElement | null>;
}) {
  const inputId = useId();
  const titleId = useId();
  const quick = selectedQuickMinUsd(filters.minUsd);
  return (
    <FilterSection name="size">
      <FilterSectionHeader
        id={titleId}
        title={FILTER_TEXT.sizeTitle}
        titleRender={<Label htmlFor={inputId} />}
        resetLabel={FILTER_TEXT.sizeReset}
        onReset={
          filters.minUsd !== null
            ? () => {
                // Ссылка сброса исчезает вместе с заданным размером: фокус — на поле.
                inputRef.current?.focus();
                setDraft({ text: "", invalid: false });
                onChange(clearFilter(filters, "minUsd"));
              }
            : undefined
        }
      />
      <Field invalid={draft.invalid} className="gap-1.5">
        <div className="relative">
          <Input
            ref={inputRef}
            id={inputId}
            inputMode="text"
            value={draft.text}
            placeholder={FILTER_TEXT.sizePlaceholder}
            className="h-[30px] pr-11 text-[13px] tabular-nums md:text-[13px]"
            onChange={(event) => {
              // Поле держит набранный текст (§66 ТЗ 1.53): в фильтр уходит
              // только прочитанное значение, нечитаемое оставляет прежний
              // фильтр и даёт подсказку.
              const edit = editMinUsdDraft(event.target.value);
              setDraft(edit.draft);
              if (edit.apply !== null) {
                onChange({ ...filters, minUsd: edit.apply.minUsd });
              }
            }}
          />
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center text-[11px] text-muted-foreground uppercase"
          >
            {FILTER_TEXT.sizeCurrency}
          </span>
        </div>
        {draft.invalid ? <FieldError match>{AMOUNT_HINT}</FieldError> : null}
      </Field>
      {/* // biome-ignore lint/a11y/useSemanticElements: ряд — сетка/flex; у <fieldset> свои правила раскладки, а имя группе даёт aria-labelledby */}
      {/* biome-ignore lint/a11y/useSemanticElements: ряд — grid/flex; у <fieldset> свои правила раскладки, имя группе даёт aria-labelledby */}
      <div role="group" aria-labelledby={titleId} className="grid grid-cols-5 gap-1">
        {MIN_USD_QUICK_VALUES.map((value) => (
          <Button
            key={value}
            variant="toggle"
            size="xs"
            aria-pressed={quick === value}
            className="rounded-sm px-0 text-xs font-medium tabular-nums"
            onClick={() => {
              const next = toggleQuickMinUsd(filters.minUsd, value);
              setDraft({ text: minUsdText(next), invalid: false });
              onChange({ ...filters, minUsd: next });
            }}
          >
            {formatChipUsd(value)}
          </Button>
        ))}
      </div>
      <span className={HINT}>{FILTER_TEXT.sizeHint}</span>
    </FilterSection>
  );
}

function StageSection({ filters, onChange, counts }: SectionsProps) {
  // Пустой выбор — «все стадии»: названия карточек тогда основным цветом.
  const noSelection = filters.states.length === 0;
  const titleId = useId();
  const firstCardRef = useRef<HTMLButtonElement>(null);
  return (
    <FilterSection name="stage">
      <FilterSectionHeader
        id={titleId}
        title={FILTER_TEXT.stageTitle}
        resetLabel={FILTER_TEXT.stageReset}
        onReset={
          !noSelection
            ? () => {
                firstCardRef.current?.focus();
                onChange(clearFilter(filters, "states"));
              }
            : undefined
        }
      />
      {/*
        Сетка равных колонок, а не перенос строк: на flex-wrap последняя
        карточка уезжала на вторую строку с шевроном в её начале (DEN-93).
        Шеврон стоит в зазоре и места в сетке не занимает. Пороги — ширина,
        при которой подпись «Approached» влезает в карточку: уже 530 px —
        три колонки и без шевронов (стрелке между рядами не на что
        указывать), уже 310 — две, уже 210 — одна. Узкой секция бывает и на
        lg: там колонка стадий делит ширину с остальными тремя.
      */}
      <div className="@container/stages">
        {/* biome-ignore lint/a11y/useSemanticElements: ряд — grid/flex; у <fieldset> свои правила раскладки, имя группе даёт aria-labelledby */}
        <div
          role="group"
          aria-labelledby={titleId}
          className="grid grid-cols-1 gap-x-3.5 gap-y-2 @min-[210px]/stages:grid-cols-2 @min-[310px]/stages:grid-cols-3 @min-[530px]/stages:grid-cols-5"
        >
          {FILTERABLE_STATES.map((state, index) => {
            const selected = filters.states.includes(state);
            return (
              <div key={state} className="relative min-w-0">
                {index > 0 ? (
                  <RiArrowRightSLine
                    aria-hidden="true"
                    className="absolute top-1/2 -left-[13px] hidden size-3 -translate-y-1/2 text-foreground/25 @min-[530px]/stages:block"
                  />
                ) : null}
                <Button
                  ref={index === 0 ? firstCardRef : undefined}
                  variant="toggle"
                  aria-pressed={selected}
                  onClick={() => onChange({ ...filters, states: toggle(filters.states, state) })}
                  className={cn(
                    "h-12 w-full min-w-0 flex-col items-start justify-center gap-[5px] rounded-md px-2.5 text-[13px] font-medium",
                    noSelection && "text-foreground",
                  )}
                >
                  {/* Подсказка — на случай увеличенного шрифта: пороги в px, и подпись
                      тогда режется многоточием. */}
                  <Tooltip>
                    <TooltipTrigger
                      render={<span className="block w-full min-w-0 truncate text-left" />}
                    >
                      {stateLabel(state)}
                    </TooltipTrigger>
                    <TooltipContent>{stateLabel(state)}</TooltipContent>
                  </Tooltip>
                  {/* Галочка — в строке счётчика: в строке подписи она отнимала
                      у «Approached» место, и в пяти колонках подпись резалась. */}
                  <span className="flex w-full min-w-0 items-center gap-2">
                    <span className="min-w-0 truncate">
                      <StageCount store={counts} stage={state} />
                    </span>
                    {selected ? (
                      <RiCheckLine aria-hidden="true" className="ml-auto size-3 shrink-0" />
                    ) : null}
                  </span>
                </Button>
              </div>
            );
          })}
        </div>
      </div>
      <span className={HINT}>{FILTER_TEXT.stageHint}</span>
    </FilterSection>
  );
}

function ExchangeSection({ filters, onChange, venues, counts }: SectionsProps) {
  const selectedVenues = filters.exchanges ?? venues;
  const active = exchangeFilterActive(filters, venues);
  const titleId = useId();
  const firstNameRef = useRef<HTMLButtonElement>(null);
  const exchanges = exchangesOf(venues);
  return (
    <FilterSection name="exchange">
      <FilterSectionHeader
        id={titleId}
        title={FILTER_TEXT.exchangeTitle}
        summary={
          active
            ? exchangeSummary(
                venues.filter((key) => selectedVenues.includes(key)).length,
                venues.length,
              )
            : FILTER_TEXT.exchangeAll
        }
        resetLabel={FILTER_TEXT.exchangeReset}
        onReset={
          active
            ? () => {
                firstNameRef.current?.focus();
                onChange(clearFilter(filters, "exchanges"));
              }
            : undefined
        }
      />
      {/* biome-ignore lint/a11y/useSemanticElements: ряд — grid/flex; у <fieldset> свои правила раскладки, имя группе даёт aria-labelledby */}
      <div
        role="group"
        aria-labelledby={titleId}
        className="grid grid-cols-[repeat(auto-fill,minmax(194px,1fr))] gap-1"
      >
        {exchanges.map((exchange, index) => {
          const markets = marketsOf(venues, exchange);
          const ownKeys = markets.map((market) => `${exchange}:${market}`);
          const enabledKeys = ownKeys.filter((key) => selectedVenues.includes(key));
          // Биржа без включённых рынков бледнее; число у неё — по всем её рынкам (§115 п. 3).
          const anyOn = enabledKeys.length > 0;
          const nameState: ExchangeToggleState = !anyOn
            ? "none"
            : enabledKeys.length === ownKeys.length
              ? "all"
              : "some";
          return (
            // Плитка — поверхность переключателя UIKit: кнопки внутри кнопки HTML не
            // допускает, поэтому вид `toggle` лежит на обёртке.
            <ToggleTile
              key={exchange}
              pressed={anyOn}
              data-exchange-row={exchange}
              // Биржу без плотностей гасит CSS по `data-empty` листа счётчика.
              className="h-[30px] gap-2 pl-2 text-[13px] font-medium not-pressed:has-data-[empty=true]:opacity-50"
            >
              <span className="flex shrink-0 gap-0.5">
                {MARKET_ORDER.map((market) => {
                  if (!markets.includes(market)) {
                    // Нет рынка — пустое место: логотипы и имена стоят на одной вертикали.
                    return <span key={market} aria-hidden="true" className="size-4 shrink-0" />;
                  }
                  const key = `${exchange}:${market}`;
                  const on = selectedVenues.includes(key);
                  return (
                    <Button
                      key={market}
                      variant="bare"
                      aria-pressed={on}
                      aria-label={marketToggleLabel(market, exchange)}
                      onClick={() =>
                        onChange({
                          ...filters,
                          exchanges: toggleMarket(filters.exchanges, key, venues),
                        })
                      }
                      className={cn(
                        // Область нажатия шире знака: на всю высоту плитки (30px) и в зазор
                        // между S и P без перекрытия (по 1px в зазор 2px, наружу 4px).
                        "relative size-4 shrink-0 rounded-[4px] p-0 after:absolute after:-inset-y-[7px]",
                        market === MARKET_ORDER[0]
                          ? "after:-right-px after:-left-1"
                          : "after:-right-1 after:-left-px",
                      )}
                    >
                      <CheckboxIndicator
                        checked={on}
                        className="size-4 text-[10px] leading-none font-bold"
                      >
                        {marketLetter(market)}
                      </CheckboxIndicator>
                    </Button>
                  );
                })}
              </span>
              <Button
                ref={index === 0 ? firstNameRef : undefined}
                variant="bare"
                aria-pressed={nameState === "some" ? "mixed" : nameState === "all"}
                aria-label={exchangeToggleLabel(exchange, nameState)}
                onClick={() =>
                  onChange({
                    ...filters,
                    exchanges: toggleExchange(filters.exchanges, exchange, venues),
                  })
                }
                className="h-full min-w-0 flex-1 justify-start gap-2 rounded-l-none rounded-r-sm px-0 pr-2.5 text-[13px] font-medium"
              >
                <ExchangeLogo exchange={exchange} decorative className="size-3.5 rounded-[3px]" />
                <span className="truncate">{exchangeLabel(exchange)}</span>
                <VenueCount
                  store={counts}
                  marketKeys={anyOn ? enabledKeys : ownKeys}
                  className="ml-auto"
                />
              </Button>
            </ToggleTile>
          );
        })}
      </div>
    </FilterSection>
  );
}

/** Строка пометки: плашка со значком, название с пояснением и переключатель. */
function MarkRow({
  icon,
  title,
  hint,
  checked,
  onCheckedChange,
  lock,
}: {
  icon: ReactNode;
  title: string;
  hint: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  /** Замок тарифа: переключатель выключен и не нажимается, рядом стоит замок с тарифом. */
  lock?: { label: string; text: string };
}) {
  const id = useId();
  return (
    <div className="flex min-h-10 items-center gap-2.5">
      <span
        aria-hidden="true"
        className={cn(
          "flex size-7 shrink-0 items-center justify-center rounded-sm bg-secondary [&_svg]:size-3.5",
          checked ? "text-foreground" : "text-muted-foreground",
        )}
      >
        {icon}
      </span>
      <div className="flex min-w-0 flex-col gap-1">
        <Label htmlFor={id} className="text-[13px] leading-none font-medium">
          {title}
        </Label>
        <span className="text-[11px] leading-[1.2] text-muted-foreground">{hint}</span>
      </div>
      {lock === undefined ? null : (
        <span className="ml-auto">
          <LockedInline label={lock.label} text={lock.text} />
        </span>
      )}
      <Switch
        id={id}
        className={lock === undefined ? "ml-auto" : undefined}
        checked={lock === undefined && checked}
        disabled={lock !== undefined}
        onCheckedChange={(next) => onCheckedChange(next === true)}
      />
    </div>
  );
}

function MarksSection({ filters, onChange }: Pick<SectionsProps, "filters" | "onChange">) {
  const planGate = usePlanGate();
  return (
    <FilterSection name="marks">
      <FilterSectionHeader title={FILTER_TEXT.marksTitle} />
      <MarkRow
        icon={<RiEyeOffLine />}
        title={FILTER_TEXT.hideMm}
        hint={FILTER_TEXT.hideMmHint}
        checked={filters.hideMm}
        onCheckedChange={(hideMm) => onChange({ ...filters, hideMm })}
        lock={
          planGate.mmApproachesLocked
            ? { label: FILTER_TEXT.hideMm, text: planGate.mmApproachesText }
            : undefined
        }
      />
      <MarkRow
        icon={<RiStarLine />}
        title={FILTER_TEXT.onlyFavorites}
        hint={FILTER_TEXT.onlyFavoritesHint}
        checked={filters.onlyFavorites}
        onCheckedChange={(onlyFavorites) => onChange({ ...filters, onlyFavorites })}
      />
    </FilterSection>
  );
}

/**
 * Четыре раздела развёрнутой панели и нижняя строка (ТЗ 1.97, §110 п. 4–6).
 * Всё применяется сразу. «Сбросить всё» возвращает только фильтры экрана:
 * личные минимумы и время до показа — отдельные окна, их панель не трогает.
 */
export function FilterSections({ filters, onChange, venues, counts }: SectionsProps) {
  const [draft, setDraft] = useState<MinUsdDraft>(() => ({
    text: minUsdText(filters.minUsd),
    invalid: false,
  }));
  // Размер сменили снаружи (крестик на фишке, «Сбросить всё») — поле следует
  // за ним; своё же набранное «1м» при этом остаётся как набрано.
  const [seenMinUsd, setSeenMinUsd] = useState(filters.minUsd);
  if (seenMinUsd !== filters.minUsd) {
    setSeenMinUsd(filters.minUsd);
    setDraft(reconcileMinUsdDraft(draft, filters.minUsd));
  }
  const activeCount = activeFilterCount(filters, venues);
  const sizeInputRef = useRef<HTMLInputElement>(null);

  return (
    <>
      {/*
        Колонка стадий — не `auto`: ряд стадий — контейнер запросов, а
        контейнер не отдаёт колонке ширину своего содержимого, и `auto`
        сжимал её до остатка (DEN-93). 570 — порог пяти колонок ряда стадий
        и отступы раздела.
      */}
      <div className="grid border-t border-border lg:grid-cols-[232px_minmax(0,570px)_minmax(320px,1fr)_236px]">
        <SizeSection
          filters={filters}
          onChange={onChange}
          draft={draft}
          setDraft={setDraft}
          inputRef={sizeInputRef}
        />
        <StageSection filters={filters} onChange={onChange} venues={venues} counts={counts} />
        <ExchangeSection filters={filters} onChange={onChange} venues={venues} counts={counts} />
        <MarksSection filters={filters} onChange={onChange} />
      </div>
      <div className="flex h-[34px] items-center justify-between border-t border-border px-4 text-xs text-muted-foreground">
        <span>{FILTER_TEXT.footerNote}</span>
        {activeCount > 0 ? (
          <Button
            variant="ghost"
            size="sm"
            className="text-foreground"
            onClick={() => {
              // Кнопка исчезает вместе с заданными фильтрами: фокус — на поле размера.
              sizeInputRef.current?.focus();
              setDraft({ text: "", invalid: false });
              onChange(defaultFilters);
            }}
          >
            <RiResetLeftLine aria-hidden="true" />
            {FILTER_TEXT.resetAll}
          </Button>
        ) : null}
      </div>
    </>
  );
}
